// Card — base visual container. Matches `.tcs-card` from tcs.css: white
// surface, 1px stone border, 12px corner radius. The `interactive` prop
// adds hover states (deeper border + soft shadow) and is appropriate when
// the whole card is clickable.
//
// Polymorphic via `as`: pass `as="a"` (with href) or `as="button"` if the
// whole card should be a link/button. Default is <div>. We render via
// React.createElement so TypeScript stays happy with the dynamic element
// type without juggling generics.

import {
  createElement,
  forwardRef,
  type ElementType,
  type MouseEventHandler,
  type ReactNode,
} from 'react';

export interface CardProps {
  children: ReactNode;
  interactive?: boolean;
  className?: string;
  as?: ElementType;
  // Pass-through — common element props. Card is polymorphic so we list the
  // commonly-used ones explicitly rather than using an index signature.
  href?: string;
  target?: string;
  rel?: string;
  role?: string;
  type?: 'button' | 'submit' | 'reset';
  id?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const Card = forwardRef<HTMLElement, CardProps>(function Card(
  { children, interactive, className, as = 'div', ...rest },
  ref,
) {
  const base =
    'rounded-xl border border-tcs-border bg-tcs-bg transition-colors duration-150';
  const interactiveCls = interactive
    ? 'hover:border-tcs-border-strong hover:shadow-tcs-card-hover'
    : '';
  const cls = `${base} ${interactiveCls} ${className ?? ''}`.trim();
  return createElement(
    as as ElementType,
    { ref, className: cls, ...rest },
    children,
  );
});
